import React from 'react';
import { Check } from 'lucide-react';
import type { ToolStep } from './types';

export interface SidebarStepRowProps {
  step: ToolStep;
  index: number;
  isCurrent: boolean;
  isDone: boolean;
  unlocked: boolean;
  onClick: () => void;
}

/** Single step row in the beige sidebar. Numbered (01, 02…), with a yellow dot
 *  for the current step and a green check for completed steps. Locked rows are
 *  dimmed and unclickable. */
export const SidebarStepRow: React.FC<SidebarStepRowProps> = ({
  step,
  index,
  isCurrent,
  isDone,
  unlocked,
  onClick,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!unlocked}
      aria-current={isCurrent ? 'step' : undefined}
      className="grid items-center gap-2.5 px-2 py-[7px] rounded-hj-sm text-left font-inherit transition-colors disabled:cursor-not-allowed"
      style={{
        gridTemplateColumns: '22px 1fr 16px',
        background: isCurrent ? 'var(--hj-beige-deep)' : 'transparent',
        borderLeft: `3px solid ${isCurrent ? 'var(--hj-yellow)' : 'transparent'}`,
        opacity: unlocked ? 1 : 0.45,
        cursor: unlocked ? 'pointer' : 'not-allowed',
      }}
    >
      <span
        className="tabular text-[10px]"
        style={{ fontFamily: 'var(--hj-font-mono)', color: 'var(--hj-beige-ink-3)' }}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <span
        className="truncate text-[13.5px]"
        style={{
          fontWeight: isCurrent ? 600 : 500,
          color: 'var(--hj-beige-ink)',
        }}
      >
        {step.label}
      </span>
      <span
        aria-hidden="true"
        className="flex items-center justify-center rounded-full"
        style={{
          width: 14,
          height: 14,
          background: isDone ? 'var(--hj-green)' : isCurrent ? 'var(--hj-yellow)' : 'transparent',
          border: isDone || isCurrent ? 'none' : '1px solid var(--hj-beige-line)',
          color: 'var(--hj-beige-ink)',
        }}
      >
        {isDone && <Check size={8} strokeWidth={2.5} />}
      </span>
    </button>
  );
};
